import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [filterWrapper]
 * @property {HTMLSelectElement} [citySelect]
 * @property {HTMLElement} storeList
 * @property {HTMLElement[]} storeItem
 * @property {HTMLElement} mapCanvas
 * @property {HTMLElement} noApiMessage
 * @property {HTMLElement} [statusMessage]
 *
 * @extends {Component<Refs>}
 */
export class CustomStoreLocator extends Component {
  requiredRefs = ['storeList', 'mapCanvas'];

  /** @type {google.maps.Map | null} */
  #map = null;

  /** @type {google.maps.InfoWindow | null} */
  #infoWindow = null;

  /** @type {{ marker: google.maps.Marker, city: string, position: google.maps.LatLngLiteral, name: string, address: string }[]} */
  #markers = [];

  /** @type {number} */
  #focusZoom = 15;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#buildCityFilter();

    const apiKey = this.dataset.apiKey;
    if (!apiKey) {
      this.refs.noApiMessage?.removeAttribute('hidden');
      return;
    }

    if (!document.querySelector('link[data-custom-store-locator-preconnect]')) {
      document.head.insertAdjacentHTML(
        'beforeend',
        '<link rel="preconnect" href="https://maps.googleapis.com" crossorigin data-custom-store-locator-preconnect>'
      );
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#observer?.disconnect();
          this.#loadGoogleMaps(apiKey);
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * Keeps the city filter and map markers in sync when the merchant adds, edits, or
   * removes "Moxie Tienda" blocks in the Theme Editor (Section Rendering API morphs the
   * DOM without a full page reload).
   */
  updatedCallback() {
    super.updatedCallback();

    this.#buildCityFilter();

    if (this.#map) {
      this.#clearMarkers();
      this.#addMarkers(this.#stores);
    } else if (window.google?.maps) {
      this.#initMap();
    }
  }

  /**
   * Builds the city filter from the rendered store items. `section.blocks` doesn't expose
   * resolved block settings from the section's own Liquid scope (same platform limitation
   * documented in custom-carousel-tabs.js), so the city list has to be read from the DOM
   * instead of computed server-side.
   */
  #buildCityFilter() {
    const select = this.refs.citySelect;
    if (!select) return;

    for (const option of [...select.options]) {
      if (option.value !== '') option.remove();
    }

    const cities = [...new Set(this.refs.storeItem.map((item) => item.dataset.city).filter(Boolean))].sort();
    if (cities.length < 2) {
      this.refs.filterWrapper?.setAttribute('hidden', '');
      return;
    }

    for (const city of cities) {
      const option = document.createElement('option');
      option.value = city ?? '';
      option.textContent = city ?? '';
      select.append(option);
    }

    this.refs.filterWrapper?.removeAttribute('hidden');
  }

  /** @param {string} apiKey */
  #loadGoogleMaps(apiKey) {
    if (window.google?.maps) {
      this.#initMap();
      return;
    }

    const callbackName = '__customStoreLocatorReady';
    const previousCallback = window[callbackName];
    window[callbackName] = () => {
      previousCallback?.();
      this.#initMap();
    };

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      previousAuthFailure?.();
      this.refs.noApiMessage?.removeAttribute('hidden');
    };

    if (document.querySelector('script[data-custom-store-locator]')) return;

    const script = document.createElement('script');
    script.dataset.customStoreLocator = 'true';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => this.refs.noApiMessage?.removeAttribute('hidden');
    document.head.append(script);
  }

  #initMap() {
    const stores = this.#stores;
    if (!stores.length) return;

    this.refs.mapCanvas.removeAttribute('hidden');

    const zoom = Number(this.dataset.zoom) || 13;

    this.#map = new google.maps.Map(this.refs.mapCanvas, {
      zoom,
      center: stores[0].position,
    });
    this.#infoWindow = new google.maps.InfoWindow();

    this.#addMarkers(stores);
  }

  /** @param {{ position: google.maps.LatLngLiteral, city: string, name: string, address: string }[]} stores */
  #addMarkers(stores) {
    this.#markers = stores.map((store) => {
      const marker = new google.maps.Marker({
        map: this.#map,
        position: store.position,
        title: store.name,
      });

      marker.addListener('click', () => this.#focusStore(store.position, marker, store.name, store.address));

      return { marker, city: store.city, position: store.position, name: store.name, address: store.address };
    });

    this.#fitToMarkers(this.#markers);
  }

  #clearMarkers() {
    for (const { marker } of this.#markers) marker.setMap(null);
    this.#markers = [];
  }

  /**
   * Fits the map to the given markers — a single store is centered close-up, several
   * stores fit all their bounds. Used on initial load and whenever the city filter
   * changes the set of visible stores, so the map always frames what the list shows.
   * @param {{ position: google.maps.LatLngLiteral }[]} entries
   */
  #fitToMarkers(entries) {
    if (!this.#map || !entries.length) return;

    if (entries.length === 1) {
      this.#map.panTo(entries[0].position);
      this.#map.setZoom(this.#focusZoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    for (const entry of entries) bounds.extend(entry.position);
    this.#map.fitBounds(bounds);
  }

  /**
   * Centers the map on a specific store and opens its info window. Used both from marker
   * clicks and from clicking a store card in the list — otherwise the map stays at the
   * zoomed-out `fitBounds` view with no way to focus a single store.
   * @param {google.maps.LatLngLiteral} position
   * @param {google.maps.Marker | undefined} marker
   * @param {string} name
   * @param {string} address
   */
  #focusStore(position, marker, name, address) {
    if (!this.#map) return;

    this.#map.panTo(position);
    this.#map.setZoom(this.#focusZoom);
    this.#infoWindow?.setContent(this.#buildInfoContent(name, address));
    this.#infoWindow?.open(this.#map, marker);
    this.#announce((this.dataset.announceFocusTemplate ?? '%%name%%').replace('%%name%%', name));
  }

  /**
   * Builds the info window content as real DOM nodes (not an HTML string) so store
   * name/address — free-text merchant settings — can never break out into markup.
   * @param {string} name
   * @param {string} address
   * @returns {HTMLElement}
   */
  #buildInfoContent(name, address) {
    const container = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = name;
    container.append(title, document.createElement('br'), document.createTextNode(address));
    return container;
  }

  /** @param {string} message */
  #announce(message) {
    if (this.refs.statusMessage) this.refs.statusMessage.textContent = message;
  }

  get #stores() {
    return this.refs.storeItem
      .map((item) => {
        const lat = Number(item.dataset.lat);
        const lng = Number(item.dataset.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;

        return {
          position: { lat, lng },
          name: item.dataset.name ?? '',
          address: item.dataset.address ?? '',
          city: item.dataset.city ?? '',
        };
      })
      .filter((store) => store !== null);
  }

  /** @param {Event} event */
  focusStoreFromList(event) {
    const item = event.target instanceof HTMLElement ? event.target.closest('.store-locator__item') : null;
    if (!(item instanceof HTMLElement)) return;

    const lat = Number(item.dataset.lat);
    const lng = Number(item.dataset.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const match = this.#markers.find((entry) => entry.position.lat === lat && entry.position.lng === lng);
    this.#focusStore({ lat, lng }, match?.marker, item.dataset.name ?? '', item.dataset.address ?? '');
  }

  /** @param {Event} event */
  filterByCity(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const city = select.value;

    this.#infoWindow?.close();

    let visibleCount = 0;
    for (const item of this.refs.storeItem) {
      const isVisible = city === '' || item.dataset.city === city;
      item.toggleAttribute('hidden', !isVisible);
      if (isVisible) visibleCount += 1;
    }

    const visibleMarkers = this.#markers.filter(({ city: markerCity }) => city === '' || markerCity === city);
    for (const { marker, city: markerCity } of this.#markers) {
      marker.setVisible(city === '' || markerCity === city);
    }

    this.#fitToMarkers(visibleMarkers);
    this.#announce(
      (this.dataset.announceResultsTemplate ?? '%%count%%').replace('%%count%%', String(visibleCount))
    );
  }
}

if (!customElements.get('custom-store-locator')) {
  customElements.define('custom-store-locator', CustomStoreLocator);
}
